export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: "webTvUserToken",
  REFRESH_TOKEN: "webTvrefreshToken",
  AUTH_PHASE: "webTvAuthPhase",
  ACTIVE_PROFILE: "webTvActiveProfile",
  QR_TEMP_TOKEN: "webTvQrTempToken",
  LOGIN_ATTEMPT_TOKEN: "webTvLoginAttemptToken",
  LOGIN_ATTEMPT_EXPIRY: "webTvLoginAttemptExpiry",
};

export const AUTH_PHASES = {
  PROFILE_SELECTION: "profile-selection",
  AUTHENTICATED: "authenticated",
};

export const AUTH_SESSION_EXPIRED_EVENT = "manabakh:auth-session-expired";

function getStorage() {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage || null;
  } catch (error) {
    return null;
  }
}

function getItem(key) {
  var storage = getStorage();
  if (!storage) return null;

  try {
    var value = storage.getItem(key);
    if (!value || value === "undefined" || value === "null") {
      return null;
    }
    return value;
  } catch (error) {
    return null;
  }
}

function setItem(key, value) {
  var storage = getStorage();
  if (!storage) return;

  try {
    if (value === undefined || value === null || value === "") {
      storage.removeItem(key);
    } else {
      storage.setItem(key, String(value));
    }
  } catch (error) {
    // Storage can be unavailable in private mode and older TV browsers.
  }
}

function removeItem(key) {
  var storage = getStorage();
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch (error) {
    // Best-effort cleanup.
  }
}

export function getAccessToken() {
  return getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
}

export function getRefreshToken() {
  return getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
}

export function getAuthPhase() {
  return getItem(AUTH_STORAGE_KEYS.AUTH_PHASE);
}

export function getLoginAttemptToken() {
  return getItem(AUTH_STORAGE_KEYS.LOGIN_ATTEMPT_TOKEN);
}

export function getLoginAttemptExpiry() {
  var value = getItem(AUTH_STORAGE_KEYS.LOGIN_ATTEMPT_EXPIRY);
  if (!value) return null;

  var parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function setLoginAttemptToken(token, expiresAt) {
  setItem(AUTH_STORAGE_KEYS.LOGIN_ATTEMPT_TOKEN, token);
  setItem(AUTH_STORAGE_KEYS.LOGIN_ATTEMPT_EXPIRY, expiresAt);
}

export function clearLoginAttemptToken() {
  removeItem(AUTH_STORAGE_KEYS.LOGIN_ATTEMPT_TOKEN);
  removeItem(AUTH_STORAGE_KEYS.LOGIN_ATTEMPT_EXPIRY);
}

export function setAuthTokens(tokens, phase) {
  if (!tokens || !tokens.accessToken) {
    throw new Error("An access token is required");
  }

  setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);

  if (Object.prototype.hasOwnProperty.call(tokens, "refreshToken")) {
    if (tokens.refreshToken) {
      setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
    } else {
      removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    }
  }

  if (phase) {
    setItem(AUTH_STORAGE_KEYS.AUTH_PHASE, phase);
  }
}

export function setTemporaryAuthTokens(tokens) {
  removeItem(AUTH_STORAGE_KEYS.ACTIVE_PROFILE);
  setAuthTokens(tokens, AUTH_PHASES.PROFILE_SELECTION);
}

export function setAuthenticatedSession(tokens, profile) {
  setAuthTokens(tokens, AUTH_PHASES.AUTHENTICATED);

  if (profile) {
    try {
      setItem(AUTH_STORAGE_KEYS.ACTIVE_PROFILE, JSON.stringify(profile));
    } catch (error) {
      removeItem(AUTH_STORAGE_KEYS.ACTIVE_PROFILE);
    }
  }
}

export function getActiveProfile() {
  var value = getItem(AUTH_STORAGE_KEYS.ACTIVE_PROFILE);
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch (error) {
    removeItem(AUTH_STORAGE_KEYS.ACTIVE_PROFILE);
    return null;
  }
}

export function hasAuthenticatedSession() {
  if (!getAccessToken()) return false;

  // Tokens stored before auth v2 have no phase marker. Treat them as final so
  // existing signed-in users are migrated without an unnecessary logout.
  return getAuthPhase() !== AUTH_PHASES.PROFILE_SELECTION;
}

export function clearAuthSession(options) {
  var includeLoginAttempt = !options || options.includeLoginAttempt !== false;

  removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  removeItem(AUTH_STORAGE_KEYS.AUTH_PHASE);
  removeItem(AUTH_STORAGE_KEYS.ACTIVE_PROFILE);
  removeItem(AUTH_STORAGE_KEYS.QR_TEMP_TOKEN);

  if (includeLoginAttempt) {
    clearLoginAttemptToken();
  }
}

export function notifySessionExpired(reason) {
  clearAuthSession();

  if (typeof window === "undefined" || !window.dispatchEvent) return;

  try {
    window.dispatchEvent(
      new CustomEvent(AUTH_SESSION_EXPIRED_EVENT, {
        detail: { reason: reason || "invalid-session" },
      })
    );
  } catch (error) {
    var event = document.createEvent("Event");
    event.initEvent(AUTH_SESSION_EXPIRED_EVENT, true, true);
    window.dispatchEvent(event);
  }
}
